import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Upload, X, Send, Package, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Custom Confirm Modal - styled to match site aesthetic
const ConfirmModal = ({ isOpen, onConfirm, onCancel, message }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90">
      <div className="bg-zinc-900 border border-teal-500/50 p-6 max-w-sm w-full">
        <p className="font-mono text-sm mb-6 text-white uppercase">{message}</p>
        <div className="flex gap-3">
          <button
            onClick={onConfirm}
            className="flex-1 py-3 bg-red-500 text-white font-mono font-bold text-sm uppercase hover:bg-red-600 transition-colors"
          >
            YES, DELETE
          </button>
          <button
            onClick={onCancel}
            className="flex-1 py-3 bg-zinc-800 border border-zinc-700 text-white font-mono text-sm uppercase hover:bg-zinc-700 transition-colors"
          >
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
};

// Image Lightbox with swipe/carousel support
const ImageLightbox = ({ images, initialIndex, onClose, getImageUrl }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  // Minimum swipe distance
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && currentIndex < images.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
    if (isRightSwipe && currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const goNext = (e) => {
    e.stopPropagation();
    if (currentIndex < images.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const goPrev = (e) => {
    e.stopPropagation();
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && currentIndex < images.length - 1) setCurrentIndex(prev => prev + 1);
      if (e.key === 'ArrowLeft' && currentIndex > 0) setCurrentIndex(prev => prev - 1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, images.length, onClose]);

  if (!images || images.length === 0) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close button */}
      <button 
        onClick={onClose} 
        className="absolute top-4 right-4 p-2 text-white z-10 bg-zinc-800/50 hover:bg-zinc-700"
      >
        <X size={24} />
      </button>

      {/* Image counter */}
      <div className="absolute top-4 left-4 text-white font-mono text-sm bg-zinc-800/50 px-3 py-1">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Navigation arrows */}
      {images.length > 1 && (
        <>
          {currentIndex > 0 && (
            <button
              onClick={goPrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-zinc-800/50 text-white hover:bg-zinc-700 z-10"
            >
              <ChevronLeft size={24} />
            </button>
          )}
          {currentIndex < images.length - 1 && (
            <button
              onClick={goNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-zinc-800/50 text-white hover:bg-zinc-700 z-10"
            >
              <ChevronRight size={24} />
            </button>
          )}
        </>
      )}

      {/* Main image with touch support */}
      <div
        className="w-full h-full flex items-center justify-center p-4"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <img 
          src={getImageUrl(images[currentIndex])}
          alt=""
          className="max-w-full max-h-full object-contain"
        />
      </div>

      {/* Dot indicators */}
      {images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === currentIndex ? 'bg-teal-500' : 'bg-zinc-600 hover:bg-zinc-500'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Image Thumbnail with loading state
const ImageThumbnail = ({ src, onRemove, onClick }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div className="relative w-14 h-14 border border-zinc-700 group bg-zinc-800">
      {!loaded && !error && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <img 
        src={src} 
        alt="" 
        className={`w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity ${loaded ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClick}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="absolute -top-1 -right-1 p-0.5 bg-red-500 hover:bg-red-600"
      >
        <X size={10} />
      </button>
    </div>
  );
};

const SubmitItems = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([createEmptyItem()]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [notes, setNotes] = useState('');
  const [paypalEmail, setPaypalEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lightboxData, setLightboxData] = useState(null); // { images: [], initialIndex: 0 }
  const [confirmDelete, setConfirmDelete] = useState(null); // { itemId: string } or null
  const [userProfile, setUserProfile] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(true);

  function createEmptyItem() {
    return {
      id: Date.now(),
      name: '',
      description: '',
      asking_price: '',
      category: '',
      brand: '',
      images: [],
      uploading: false
    };
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Check if user is international
  useEffect(() => {
    const checkUserAccess = async () => {
      const token = getToken();
      if (!token) {
        navigate('/login');
        return;
      }
      
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setUserProfile(data);
          if (data.is_international) {
            toast.error('TRADE/SELL UNAVAILABLE FOR INTERNATIONAL CUSTOMERS');
          }
        }
      } catch (err) {
        console.error('Failed to check user access:', err);
      } finally {
        setCheckingAccess(false);
      }
    };
    
    checkUserAccess();
  }, [navigate]);

  useEffect(() => {
    fetchCategories();
    fetchBrands();
  }, []);

  const getToken = () => {
    return localStorage.getItem('blkmrkt_token') || sessionStorage.getItem('blkmrkt_token');
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/products/categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchBrands = async () => {
    try {
      const res = await fetch(`${API_URL}/api/brands`);
      if (res.ok) {
        const data = await res.json();
        setBrands(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch brands:', err);
    }
  };

  const updateItem = (itemId, field, value) => {
    setItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  const addItem = () => {
    setItems(prev => [...prev, createEmptyItem()]);
  };

  const removeItem = (itemId) => {
    if (items.length > 1) {
      setConfirmDelete({ itemId });
    }
  };

  const confirmRemoveItem = () => {
    if (confirmDelete?.itemId) {
      setItems(prev => prev.filter(item => item.id !== confirmDelete.itemId));
      toast.success('ITEM REMOVED', { duration: 2000 });
      setConfirmDelete(null);
    }
  };

  const handleImageUpload = async (itemId, files) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    if (item.images.length + files.length > 5) {
      setError('MAX 5 IMAGES PER ITEM');
      return;
    }

    updateItem(itemId, 'uploading', true);
    const token = getToken();

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch(`${API_URL}/api/upload/user-image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (res.ok) {
          const data = await res.json();
          setItems(prev => prev.map(i => 
            i.id === itemId 
              ? { ...i, images: [...i.images, data.url] }
              : i
          ));
        } else {
          const errData = await res.json().catch(() => ({}));
          setError(errData.detail || 'UPLOAD FAILED');
        }
      } catch (err) {
        console.error('Upload failed:', err);
        setError('UPLOAD FAILED. PLEASE TRY AGAIN.');
      }
    }

    updateItem(itemId, 'uploading', false);
  };

  const removeImage = (itemId, imageIndex) => {
    setItems(prev => prev.map(item => 
      item.id === itemId 
        ? { ...item, images: item.images.filter((_, idx) => idx !== imageIndex) }
        : item
    ));
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    // Validate PayPal email
    if (!paypalEmail.trim() || !paypalEmail.includes('@')) {
      setError('VALID PAYPAL EMAIL REQUIRED');
      return;
    }

    for (const item of items) {
      if (!item.name.trim()) {
        setError('ALL ITEMS NEED A NAME');
        return;
      }
      if (!item.description.trim()) {
        setError('ALL ITEMS NEED A DESCRIPTION');
        return;
      }
      if (!item.asking_price || parseFloat(item.asking_price) <= 0) {
        setError('ALL ITEMS NEED A PRICE');
        return;
      }
      if (!item.category) {
        setError('ALL ITEMS NEED A CATEGORY');
        return;
      }
      if (!item.brand) {
        setError('ALL ITEMS NEED A BRAND');
        return;
      }
    }

    setIsSubmitting(true);
    const token = getToken();

    if (!token) {
      setError('PLEASE LOG IN');
      setIsSubmitting(false);
      navigate('/login');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          items: items.map(item => ({
            name: item.name.toUpperCase(),
            description: item.description.toUpperCase(),
            asking_price: parseFloat(item.asking_price),
            category: item.category,
            brand: item.brand,
            images: item.images
          })),
          notes: notes ? notes.toUpperCase() : null,
          paypal_email: paypalEmail
        })
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(`SUBMITTED! ID: ${data.submission_id}`);
        setItems([createEmptyItem()]);
        setNotes('');
        setPaypalEmail('');
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        setError(data.detail?.toUpperCase() || 'FAILED');
      }
    } catch (err) {
      setError('FAILED. TRY AGAIN.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getImageUrl = (img) => {
    if (!img) return '';
    if (img.startsWith('http')) return img;
    return `${API_URL}${img}`;
  };

  // Custom Select
  const CustomSelect = ({ value, onChange, options, placeholder, required = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    
    return (
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 text-sm text-left font-mono uppercase flex items-center justify-between"
        >
          <span className={value ? 'text-white' : 'text-zinc-500'}>
            {value || placeholder}{required && !value ? ' *' : ''}
          </span>
          <ChevronDown size={16} className={`text-zinc-500 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {isOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
            <div className="absolute z-50 w-full bg-zinc-900 border border-zinc-700 max-h-40 overflow-y-auto shadow-xl bottom-full mb-1">
              {options.map((opt, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-left font-mono uppercase text-sm border-b border-zinc-800 last:border-0 ${
                    value === opt.value ? 'bg-teal-500/20 text-teal-400' : 'text-white'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  // Show loading while checking access
  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <p className="font-mono text-zinc-500">LOADING...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Lightbox */}
      {lightboxData && (
        <ImageLightbox
          images={lightboxData.images}
          initialIndex={lightboxData.initialIndex}
          onClose={() => setLightboxData(null)}
          getImageUrl={getImageUrl}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!confirmDelete}
        message="ARE YOU SURE YOU WANT TO DELETE THIS ITEM?"
        onConfirm={confirmRemoveItem}
        onCancel={() => setConfirmDelete(null)}
      />

      {/* Header */}
      <div className="sticky top-0 z-10 bg-black border-b border-zinc-800">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-base font-bold font-mono">SUBMIT ITEMS</h1>
            <p className="text-[10px] text-zinc-500 font-mono">WE'LL MAKE AN OFFER</p>
          </div>
        </div>
      </div>

      <div className="flex-1 max-w-lg mx-auto px-4 py-4 space-y-4 w-full">
        {/* Messages */}
        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-2 font-mono text-xs">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-500/20 border border-green-500 text-green-400 px-3 py-2 font-mono text-xs">
            {success}
          </div>
        )}

        {/* PayPal Email */}
        <div className="bg-zinc-900 border border-zinc-800 p-3">
          <label className="block text-xs text-zinc-500 font-mono mb-1">PAYPAL EMAIL *</label>
          <input
            type="email"
            value={paypalEmail}
            onChange={(e) => setPaypalEmail(e.target.value)}
            onBlur={(e) => setPaypalEmail(e.target.value.toLowerCase())}
            placeholder="YOUR PAYPAL EMAIL FOR PAYMENT"
            className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 text-sm text-white placeholder-zinc-500 font-mono"
          />
          <p className="text-[10px] text-zinc-600 font-mono mt-1">WE'LL SEND PAYMENT HERE IF WE BUY YOUR ITEMS</p>
        </div>

        {/* Items */}
        {items.map((item, index) => (
          <div key={item.id} className="bg-zinc-900 border border-zinc-800 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-500 font-mono">ITEM {index + 1}</span>
              {items.length > 1 && (
                <button onClick={() => removeItem(item.id)} className="text-red-400 p-1">
                  <Trash2 size={16} />
                </button>
              )}
            </div>

            {/* Name */}
            <input
              type="text"
              value={item.name}
              onChange={(e) => updateItem(item.id, 'name', e.target.value)}
              onBlur={(e) => updateItem(item.id, 'name', e.target.value.toUpperCase())}
              placeholder="ITEM NAME *"
              className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 text-sm text-white placeholder-zinc-500 font-mono uppercase"
            />

            {/* Description */}
            <textarea
              value={item.description}
              onChange={(e) => updateItem(item.id, 'description', e.target.value)}
              onBlur={(e) => updateItem(item.id, 'description', e.target.value.toUpperCase())}
              placeholder="DESCRIPTION, CONDITION, FLAWS *"
              rows={2}
              className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 text-sm text-white placeholder-zinc-500 resize-none font-mono uppercase"
            />

            {/* Price & Category */}
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                min="0"
                step="0.01"
                value={item.asking_price}
                onChange={(e) => updateItem(item.id, 'asking_price', e.target.value)}
                placeholder="PRICE $ *"
                className="bg-zinc-800 border border-zinc-700 px-3 py-2 text-sm text-white placeholder-zinc-500 font-mono"
              />
              <CustomSelect
                value={item.category}
                onChange={(val) => updateItem(item.id, 'category', val)}
                placeholder="CATEGORY"
                required={true}
                options={categories.map(cat => ({ value: cat, label: cat.toUpperCase() }))}
              />
            </div>

            {/* Brand - REQUIRED */}
            <CustomSelect
              value={item.brand}
              onChange={(val) => updateItem(item.id, 'brand', val)}
              placeholder="BRAND"
              required={true}
              options={brands.map(brand => ({ value: brand.name, label: brand.name.toUpperCase() }))}
            />

            {/* Images */}
            <div>
              {item.images.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {item.images.map((img, idx) => (
                    <ImageThumbnail
                      key={idx}
                      src={getImageUrl(img)}
                      onRemove={() => removeImage(item.id, idx)}
                      onClick={() => setLightboxData({ images: item.images, initialIndex: idx })}
                    />
                  ))}
                </div>
              )}

              {item.images.length < 5 && (
                <label className="block border border-dashed border-zinc-700 p-3 text-center cursor-pointer">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleImageUpload(item.id, Array.from(e.target.files))}
                    className="hidden"
                    disabled={item.uploading}
                  />
                  {item.uploading ? (
                    <span className="text-teal-400 font-mono text-xs flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-teal-400 border-t-transparent rounded-full animate-spin"></span>
                      UPLOADING...
                    </span>
                  ) : (
                    <span className="text-zinc-500 font-mono text-xs flex items-center justify-center gap-1">
                      <Upload size={14} /> ADD PHOTOS ({5 - item.images.length} LEFT)
                    </span>
                  )}
                </label>
              )}
            </div>
          </div>
        ))}

        {/* Add Item - More prominent button */}
        <button
          onClick={addItem}
          className="w-full py-4 border-2 border-dashed border-teal-500/50 text-teal-400 font-mono text-sm flex items-center justify-center gap-2 hover:bg-teal-500/10 transition-colors"
        >
          <Plus size={20} /> ADD ANOTHER ITEM
        </button>

        {/* Notes */}
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={(e) => setNotes(e.target.value.toUpperCase())}
          placeholder="NOTES (OPTIONAL)"
          rows={2}
          className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 text-sm text-white placeholder-zinc-500 resize-none font-mono uppercase"
        />

        {/* Summary */}
        <div className="bg-zinc-900 border border-teal-500/30 p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="text-teal-400" size={16} />
            <span className="font-mono text-sm">{items.length} ITEM{items.length !== 1 ? 'S' : ''}</span>
          </div>
          <span className="text-teal-400 font-mono text-sm">
            ${items.reduce((sum, item) => sum + (parseFloat(item.asking_price) || 0), 0).toFixed(2)}
          </span>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full py-3 bg-teal-500 text-black font-bold font-mono text-sm flex items-center justify-center gap-2 disabled:bg-zinc-700 disabled:text-zinc-500"
        >
          {isSubmitting ? (
            <>
              <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
              SUBMITTING...
            </>
          ) : (
            <><Send size={16} /> SUBMIT</>
          )}
        </button>

        <p className="text-center text-[10px] text-zinc-600 font-mono pb-12">
          WE'LL RESPOND WITHIN 24-48 HOURS
        </p>
      </div>

      {/* Bottom spacer to prevent white gap */}
      <div className="bg-black h-safe-area-inset-bottom min-h-[env(safe-area-inset-bottom)]" />
    </div>
  );
};

export default SubmitItems;
